import axios from "axios"

import {getToken, renewToken, getAuthMethod, handleExpiredJwtSession} from "./useToken"

const api = axios.create({
    baseURL: `${window.__APP_CONFIG__?.API_URL ?? ""}/api`,
    withCredentials: true,
})

// Endpoints where a 401 is expected or harmless and must not force a redirect to login.
// Adjust "login" to match your real login/OIDC endpoint names.
const PUBLIC_PATHS = new Set(["login", "me", "logout", "csrf-token"])

const normalizePath = (url = "") => url.replace(/^\/+/, "").split("?")[0]

// Several requests can fail together; only redirect once.
let redirecting = false
const forceLogin = () => {
    // Already on the login page: redirecting again would just reload it
    if (redirecting || window.location.pathname.startsWith("/login")) {
        return
    }
    redirecting = true
    handleExpiredJwtSession()
}

// Add access_token to every api request
api.interceptors.request.use(
    (config) => {
        const token = getToken(true)
        // Only attach Bearer header if we're using header-based auth (password flow).
        // For cookie-based auth (OIDC), the cookie is sent automatically via withCredentials.
        if (token.access_token && getAuthMethod() === "bearer") {
            config.headers.Authorization = `Bearer ${token.access_token}`
        }
        return config
    },
    (error) => Promise.reject(error)
)

// Force back to login page when the session is no longer valid (401).
// Renew access_token when the server sends a fresh one.
api.interceptors.response.use(
    (response) => {
        const newToken = response.data?.access_token
        if (newToken) {
            renewToken(newToken)
        }
        return response
    },
    (error) => {
        const path = normalizePath(error.config?.url)

        if (error.response?.status === 401 && !PUBLIC_PATHS.has(path)) {
            forceLogin()
        } else {
            const newToken = error.response?.data?.access_token
            if (newToken) {
                renewToken(newToken)
            }
        }

        return Promise.reject(error)
    }
)

const getCsrfToken = async () => {
    // withCredentials is already set on the axios instance
    const response = await api.get("/csrf-token")
    return response.data.csrf_token
}

const wrappedRequest = async (method, route, payload, headers) => {
    // Copy so we never mutate the caller's headers object
    const hdrs = {...(headers || {})}

    if (method !== "get") {
        hdrs["X-CSRFToken"] = await getCsrfToken()
    }

    return api.request({
        method: method,
        url: route,
        data: payload,
        headers: hdrs,
    })
}

const basicUpload = async (url, formData) => {
    const csrfToken = await getCsrfToken()

    return api.post(url, formData, {
        headers: {
            "X-CSRFToken": csrfToken,
        },
    })
}

export function wrappedGet(route) {
    return wrappedRequest("get", route)
}

export function wrappedPost(route, payload, headers) {
    return wrappedRequest("post", route, payload, headers)
}

export function wrappedPut(route, payload) {
    return wrappedRequest("put", route, payload)
}

export function wrappedDelete(route) {
    return wrappedRequest("delete", route)
}

export function wrappedPatch(route, payload) {
    return wrappedRequest("patch", route, payload)
}

export function uploadFile(url, formData) {
    return basicUpload(url, formData)
}
